// Copyright 2018 Sisense Inc. All rights reserved.

/**
 * @fileoverview Sisense SDK utilities. Implementation based on Google Closure
 * Library.
 * @link https://google.github.io/closure-library/api/goog.html
 * @author artem.lit@sisense.com (Artem Lytvynov)
 * @copyright sisense@sisense.com (Sisense Ltd.)
 * @license Sisense License
 * @module @sbi/core.utils
 */

/**
 * Determine whether to use a Chrome app CSP-compliant method for loading
 * scripts or not.
 * @type {boolean}
 * @private
 */
const CSP_COMPLIANT_ = true;

/**
 * An array of loaded scripts paths.
 * @type {Array<string>}
 * @private
 */
const PATHS_ = [];

/**
 * Tries to detect whether is in the context of an HTML document.
 * @return {boolean} True if it looks like HTML document.
 * @private
 */
function inHtmlDocument_() {
    /** @type {Document} */
    const doc = window.document;
    return doc != null && 'write' in doc; // XULDocument misses write.
}

/**
 * Writes a new script pointing to {@code src} directly into the DOM.
 * NOTE: This method is not CSP-compliant. @see goog.appendScript_ for
 * the fallback mechanism.
 * @param {string} src The script URL.
 * @private
 */
function writeScript_(src) {
    window.document.write(
        `<script type="text/javascript" src="${src}"></script>`
    );
}

/**
 * Appends a new script node to the DOM using a CSP-compliant mechanism.
 * NOTE: This method is not analogous to using document.write to insert a
 * <script> tag; specifically, the user agent will execute a script added by
 * document.write immediately after the current script block finishes
 * executing, whereas the DOM-appended script node will not be executed
 * until the entire document is parsed and executed. That is to say, this
 * script is added to the end of the script execution queue.
 * @param {string} src The script URL.
 * @param {Function} resolve Function to resolve promise.
 * @param {Function} reject Function to reject promise.
 * @private
 */
function appendScript_(src, resolve, reject) {
    /** @type {Document} */
    const doc = window.document;
    const scriptEl = /** @type {HTMLScriptElement} */ (doc.createElement('script'));
    scriptEl.type = 'text/javascript';
    scriptEl.src = src;
    scriptEl.defer = false;
    scriptEl.async = true;
    scriptEl.onload = () => {
        resolve();
    };
    scriptEl.onerror = (e) => {
        reject(`Failed to load resource: ${e.target.baseURI}`);
    };
    doc.head.appendChild(scriptEl);
}

/**
 * The default implementation of the import function. Writes a script tag to
 * import the script.
 * @param {string} src The script url.
 * @param {Function} resolve Function to resolve promise.
 * @param {Function} reject Function to reject promise.
 * @return {boolean} True if the script was imported, false otherwise.
 * @private
 */
function addScript_(src, resolve, reject) {
    if (~PATHS_.indexOf(src)) {
        resolve();
        return true;
    }
    PATHS_.push(src);
    if (inHtmlDocument_()) {
        if (CSP_COMPLIANT_) {
            appendScript_(src, resolve, reject);
        } else {
            writeScript_(src);
        }
        return true;
    } else {
        return false;
    }
}

/**
 * Function for link tag insertion into the head to load css
 * @param {string} src The style url.
 * @protected
 */
function loadCss(src) {
    if (document.getElementById(src)) {
        return;
    }
    const head = document.getElementsByTagName('head')[0];
    const link = document.createElement('link');
    link.id = src;
    link.rel = 'stylesheet';
    link.type = 'text/css';
    link.href = src;
    link.media = 'all';
    head.appendChild(link);
}

/**
 * Implements a system for the JavaScript lazy loading.
 * @param {string} src Script path.
 * @returns {Promise}
 * @protected
 */
function load(src) {
    return new Promise((resolve, reject) => {
        addScript_(src, resolve, reject);
    });
}

/**
 * Name for unique ID property. Initialized in a way to help avoid
 * collisions with other closure JavaScript on the same page.
 * @type {string}
 * @private
 */
const UID_PROPERTY_ = `sisense_uid_ ${((Math.random() * 1e9) >>> 0)}`;

/**
 * Counter for UID.
 * @type {number}
 * @private
 */
let uidCounter_ = 0;

/**
 * Gets a unique ID for an object. This mutates the object so that further
 * calls with the same object as a parameter returns the same value. The
 * unique ID is guaranteed to be unique across the current session amongst
 * objects that are passed into {@code getUid}. There is no guarantee that
 * the ID is unique or consistent across sessions. It is unsafe to generate
 * unique ID for function prototypes.
 * @param {Object} obj The object to get the unique ID for.
 * @return {number} The unique ID for the object.
 * @protected
 */
const getUid = (obj) => {
    // In Opera window.hasOwnProperty exists but always returns false so we
    // avoid using it. As a consequence the unique ID generated for
    // BaseClass.prototype and SubClass.prototype will be the same.
    if (!obj[UID_PROPERTY_]) {
        obj[UID_PROPERTY_] = ++uidCounter_;
    }
    return obj[UID_PROPERTY_];
};

/**
 * This is a "fixed" version of the typeof operator.  It differs from the
 * typeof operator in such a way that null returns 'null' and arrays return
 * 'array'.
 * @param {?} value The value to get the type of.
 * @return {string} The name of the type.
 * @protected
 */
const typeOf = (value) => {
    const s = typeof value;
    if (s === 'object') {
        if (value) {
            // Check these first, so we can avoid calling
            // Object.prototype.toString if possible.
            //
            // IE improperly marshals typeof across execution contexts, but
            // a cross-context object will still return false for
            // "instanceof Object".
            if (value instanceof Array) {
                return 'array';
            } else {
                if (value instanceof Object) {
                    return s;
                }
            }

            // HACK: In order to use an Object prototype method on the
            // arbitrary value, the compiler requires the value be cast to
            // type Object, even though the ECMA spec explicitly allows it.
            const className = Object.prototype.toString.call(
                /** @type {!Object} */ (value)
            );

            // In Firefox 3.6, attempting to access iframe window objects'
            // length property throws an NS_ERROR_FAILURE, so we need to
            // special-case it here.
            if (className === '[object Window]') {
                return 'object';
            }

            // We cannot always use constructor == Array or instanceof Array
            // because different frames have different Array objects. In
            // IE6, if the iframe where the array was created is destroyed,
            // the array loses its prototype. Then de-referencing val.splice
            // here throws an exception, so we can't use isFunction.
            // Calling typeof directly returns 'unknown' so that will work.
            // In this case, this function will return false and most array
            // functions will still work because the array is still
            // array-like (supports length and []) even though it has lost
            // its prototype.
            // Mark Miller noticed that Object.prototype.toString
            // allows access to the unforgeable [[Class]] property.
            // 15.2.4.2 Object.prototype.toString ( )
            // When the toString method is called, the following steps are
            // taken:
            // 1. Get the [[Class]] property of this object.
            // 2. Compute a string value by concatenating the three strings
            //    "[object ", Result(1), and "]".
            // 3. Return Result(2).
            // and this behavior survives the destruction of the execution
            // context.
            if ((className === '[object Array]'
                // In IE all non value types are wrapped as objects
                // across window boundaries (not iframe though) so we
                // have to do object detection for this edge case.
                || typeof value.length === 'number'
                && typeof value.splice !== 'undefined'
                && typeof value.propertyIsEnumerable !== 'undefined'
                && !value.propertyIsEnumerable('splice'))) {
                return 'array';
            }

            // HACK: There is still an array case that fails.
            //     function ArrayImpostor() {}
            //     ArrayImpostor.prototype = [];
            //     var impostor = new ArrayImpostor;
            // this can be fixed by getting rid of the fast path
            // (value instanceof Array) and solely relying on
            // (value && Object.prototype.toString.val(value) ===
            // '[object Array]') but that would require many more function
            // calls and is not warranted unless closure code is receiving
            // objects from untrusted sources.

            // IE in cross-window calls does not correctly marshal the
            // function type (it appears just as an object) so we cannot use
            // just typeof val == 'function'. However, if the object has a
            // call property, it is a function.
            if ((className === '[object Function]'
                || typeof value.call !== 'undefined'
                && typeof value.propertyIsEnumerable !== 'undefined'
                && !value.propertyIsEnumerable('call'))) {
                return 'function';
            }
        } else {
            return 'null';
        }
    } else if (s === 'function' && typeof value.call === 'undefined') {
        // In Safari typeof nodeList returns 'function', and on Firefox
        // typeof behaves similarly for HTML{Applet,Embed,Object}, Elements
        // and RegExps. We would like to return object for those and we can
        // detect an invalid function by making sure that the function
        // object has a call method.
        return 'object';
    }
    return s;
};

/**
 * Returns true if the specified value is not undefined.
 * WARNING: Do not use this to test if an object has a property. Use the in
 * operator instead.
 * void 0 always evaluates to undefined and hence we do not need to depend on
 * the definition of the global variable named 'undefined'.
 * @param {?} val Variable to test.
 * @return {boolean} Whether variable is defined.
 * @protected
 */
const isDef = (val) => {
    return val !== void 0; // eslint-disable-line no-void
};

/**
 * Returns true if the specified value is null.
 * @param {?} val Variable to test.
 * @return {boolean} Whether variable is null.
 * @protected
 */
const isNull = (val) => {
    return val === null;
};

/**
 * Returns true if the specified value is defined and not null.
 * @param {?} val Variable to test.
 * @return {boolean} Whether variable is defined and not null.
 * @protected
 */
const isDefAndNotNull = (val) => {
    // Note that undefined == null.
    return val != null;
};

/**
 * Returns true if the specified value is an array.
 * @param {?} val Variable to test.
 * @return {boolean} Whether variable is an array.
 * @protected
 */
const isArray = (val) => {
    return typeOf(val) === 'array';
};

/**
 * Returns true if the object looks like an array. To qualify as array like
 * the value needs to be either a NodeList or an object with a Number length
 * property. As a special case, a function value is not array like, because
 * its length property is fixed to correspond to the number of expected
 * arguments.
 * @param {?} val Variable to test.
 * @return {boolean} Whether variable is an array.
 * @protected
 */
const isArrayLike = (val) => {
    const type = typeOf(val);
    // We do not use isObject here in order to exclude function values.
    return type === 'array' || type === 'object' && typeof val.length === 'number';
};

/**
 * Returns true if the specified value is a string.
 * @param {?} val Variable to test.
 * @return {boolean} Whether variable is a string.
 * @protected
 */
const isString = (val) => {
    return typeof val === 'string';
};

/**
 * Returns true if the specified value is a boolean.
 * @param {?} val Variable to test.
 * @return {boolean} Whether variable is boolean.
 * @protected
 */
const isBoolean = (val) => {
    return typeof val === 'boolean';
};

/**
 * Returns true if the specified value is a number.
 * @param {?} val Variable to test.
 * @return {boolean} Whether variable is a number.
 * @protected
 */
const isNumber = (val) => {
    return typeof val === 'number';
};

/**
 * Returns true if the specified value is a function.
 * @param {?} val Variable to test.
 * @return {boolean} Whether variable is a function.
 * @protected
 */
const isFunction = (val) => {
    return typeOf(val) === 'function';
};

/**
 * Returns true if the specified value is an object.  This includes arrays and
 * functions.
 * @param {?} val Variable to test.
 * @return {boolean} Whether variable is an object.
 * @protected
 */
const isObject = (val) => {
    const type = typeof val;
    return type === 'object' && val != null || type === 'function';
    // return Object(val) === val also works, but is slower, especially
    // if val is not an object.
};

/**
 * Returns true if the specified value is an object-like. A value is the
 * object-like if it's not null and has a typeof of 'object'.
 * @param {?} val Variable to test.
 * @return {boolean} Whether variable is an object-like.
 * @protected
 */
const isObjectLike = (val) => {
    return typeof val === 'object' && val !== null;
};

/**
 * Returns true if the object looks like a Date. To qualify as Date-like the
 * value needs to be an object and have a getFullYear() function.
 * @param {?} val Variable to test.
 * @return {boolean} Whether variable is a like a Date.
 * @protected
 */
const isDateLike = (val) => {
    return isObject(val) && typeof val.getFullYear === 'function';
};

// Exports:

exports.load = load;
exports.getUid = getUid;
exports.typeOf = typeOf;
exports.isDef = isDef;
exports.isNull = isNull;
exports.isDefAndNotNull = isDefAndNotNull;
exports.isArray = isArray;
exports.isArrayLike = isArrayLike;
exports.isDateLike = isDateLike;
exports.isString = isString;
exports.isBoolean = isBoolean;
exports.isNumber = isNumber;
exports.isFunction = isFunction;
exports.isObject = isObject;
exports.isObjectLike = isObjectLike;
exports.loadCss = loadCss;
